/**
 * /dashboard — KPIs, gráficos Recharts y paneles.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { PageHeader } from '@/components/app-shell'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { KpiCard } from '@/components/ui/kpi-card'
import { Skeleton } from '@/components/ui/skeleton'
import { StatusPill } from '@/components/ui/status-pill'
import { getDashboard } from '@/lib/api'

const CHART = ['#1C65C8', '#009891', '#409D48', '#DF911A', '#D33949']

export function DashboardPage() {
  const query = useQuery({
    queryKey: ['dashboard'],
    queryFn: getDashboard,
    refetchInterval: 60_000,
  })

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-72" />
      </div>
    )
  }

  if (query.isError || !query.data) {
    return (
      <Card>
        <EmptyState
          title="No se pudo cargar el dashboard"
          description={(query.error as Error | null)?.message}
        />
      </Card>
    )
  }

  const d = query.data

  return (
    <>
      <PageHeader
        title="Fleet dashboard"
        subtitle="Salud de la flota, reparaciones abiertas y gasto"
      />

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          label="Active units"
          value={d.kpis.activeVehicles}
          hint={`de ${d.kpis.totalVehicles} total`}
        />
        <KpiCard label="Open work orders" value={d.kpis.openWorkOrders} />
        <KpiCard label="Failed insp. 30d" value={d.kpis.failedInspections30d} />
        <KpiCard
          label="Cost 12 months"
          value={`$${Math.round(d.kpis.cost12m).toLocaleString()}`}
        />
      </div>

      <div className="mb-4 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Maintenance cost trend (12 months)" />
          <CardBody className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.monthlyCost}>
                <CartesianGrid strokeDasharray="3 3" stroke="#DEE2E7" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="repairs"
                  name="Repairs"
                  stroke={CHART[0]}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="other"
                  name="Other"
                  stroke={CHART[1]}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Fleet by type" />
          <CardBody className="h-64">
            {d.fleetByType.length === 0 ? (
              <EmptyState className="py-8" title="Sin datos" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={d.fleetByType}
                    dataKey="count"
                    nameKey="type"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ type, percent }) =>
                      `${type} ${((percent ?? 0) * 100).toFixed(0)}%`
                    }
                  >
                    {d.fleetByType.map((_, i) => (
                      <Cell key={i} fill={CHART[i % CHART.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="mb-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Highest cost units" />
          <CardBody className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.topCostUnits}>
                <CartesianGrid strokeDasharray="3 3" stroke="#DEE2E7" />
                <XAxis dataKey="unitNumber" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="total" name="Total $" fill={CHART[0]} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Fleet status" />
          <CardBody className="space-y-2">
            {d.fleetStatus.map((s) => (
              <div key={s.status} className="flex items-center justify-between text-sm">
                <StatusPill status={s.status} />
                <span className="font-medium">{s.count}</span>
              </div>
            ))}
          </CardBody>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel
          title="Maintenance alerts"
          empty="Sin alertas"
          items={d.maintenanceAlerts.map((a) => ({
            key: `${a.unitNumber}-${a.name}`,
            primary: `Unit ${a.unitNumber} · ${a.name}`,
            pill: a.dueState,
          }))}
        />
        <Panel
          title="Priority repairs"
          empty="Sin reparaciones prioritarias"
          items={d.priorityRepairs.map((w) => ({
            key: w.id,
            primary: w.title,
            secondary: `Unit ${w.unitNumber} · ${w.workOrderNumber}`,
            pill: w.priority,
            to: `/work-orders/${w.id}`,
          }))}
        />
        <Panel
          title="Failed inspections"
          empty="Sin fallos recientes"
          items={d.failedInspections.map((i) => ({
            key: i.id,
            primary: `Unit ${i.unitNumber}`,
            secondary: new Date(i.datetime).toLocaleString(),
            pill: i.status,
            to: `/inspections/${i.id}`,
          }))}
        />
        <Card>
          <CardHeader title="Quick links" />
          <CardBody className="flex flex-wrap gap-3 text-sm">
            <Link to="/fleet" className="font-medium text-primary">
              Fleet
            </Link>
            <Link to="/inspections/new" className="font-medium text-primary">
              New inspection
            </Link>
            <Link to="/work-orders" className="font-medium text-primary">
              Work orders
            </Link>
            <Link to="/reports" className="font-medium text-primary">
              Reports
            </Link>
          </CardBody>
        </Card>
      </div>
    </>
  )
}

function Panel({
  title,
  empty,
  items,
}: {
  title: string
  empty: string
  items: { key: string; primary: string; secondary?: string; pill: string; to?: string }[]
}) {
  return (
    <Card>
      <CardHeader title={title} />
      <CardBody className="divide-y divide-border p-0">
        {items.length === 0 ? (
          <EmptyState className="py-8" title={empty} />
        ) : (
          items.map((it) => {
            const inner = (
              <>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{it.primary}</span>
                  {it.secondary && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {it.secondary}
                    </span>
                  )}
                </span>
                <StatusPill status={it.pill} />
              </>
            )
            return it.to ? (
              <a
                key={it.key}
                href={it.to}
                className="flex min-h-[52px] items-center gap-3 px-4 py-2.5 hover:bg-muted"
              >
                {inner}
              </a>
            ) : (
              <div key={it.key} className="flex min-h-[52px] items-center gap-3 px-4 py-2.5">
                {inner}
              </div>
            )
          })
        )}
      </CardBody>
    </Card>
  )
}
